import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import React, { useRef, useEffect, useMemo } from 'react';
import { AllCommunityModule, DomLayoutType, GridOptions, ModuleRegistry, RowModelType } from 'ag-grid-community';
import { AllEnterpriseModule, ContextMenuModule, LicenseManager } from 'ag-grid-enterprise';

LicenseManager.setLicenseKey('Using_this_{AG_Grid}_Enterprise_key_{AG-070050}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Atlas_Systems_Private_Limited}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{23_October_2025}____[v3]_[01]_MTc2MTE3NDAwMDAwMA==1d81cd16c7815f67f5d1f9988f712a5f');
ModuleRegistry.registerModules([AllEnterpriseModule, ContextMenuModule, AllCommunityModule]);

// Extend GridOptions to include all AgGridReact props
export interface AgGridTableProps extends GridOptions {
  /** Custom component props */
  gridHeight?: number | string;
  className?: string;
  style?: React.CSSProperties;
  domLayout?: DomLayoutType;
  rowData?: any[];
  columnDefs?: any[];
  rowModelType?: RowModelType;
  datasource?: any;
  onGridReady?: (params: any) => void;
  rowHeight?: number;
  pagination?: boolean;
  paginationPageSize?: number;
  paginationPageSizeOptions?: number[];
  suppressPaginationPanel?: boolean;
  suppressContextMenu?: boolean;
  /** When true the grid becomes fully read-only: no selection, no editing, no checkbox/header checkbox. */
  readOnly?: boolean;
  [key: string]: any;
}

const AgGridTable: React.FC<AgGridTableProps> = ({
  rowData,
  columnDefs,
  gridHeight = '100%',
  className = '',
  style = {},
  domLayout = 'autoHeight',
  rowModelType = 'clientSide',
  datasource,
  onGridReady,
  rowHeight = 56,
  pagination = true,
  paginationPageSize = 10,
  paginationPageSizeOptions,
  suppressPaginationPanel = false,
  suppressContextMenu = true,
  readOnly = false,
  ...rest
}) => {
  const gridRef = useRef<any>(null);

  // Derive processed column definitions for read-only mode
  const processedColumnDefs = useMemo(() => {
    if (!Array.isArray(columnDefs)) return columnDefs;

    return columnDefs.map((col: any) => {
      return {
        ...col,

        tooltipValueGetter: params => {
          if (!params || params.value == null) return null;

          const text = String(params.value);
          const colWidth = params.column.getActualWidth() - 16; // padding

          // Create hidden canvas to measure text width
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          ctx.font = "13px Jost"; // match grid cell font

          const textWidth = ctx.measureText(text).width;

          // Show tooltip ONLY if text overflows
          return textWidth > colWidth ? text : null;
        },

        tooltipComponent: undefined // use default AG tooltip
      };
    });
  }, [columnDefs, readOnly]);


  useEffect(() => {
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      .ag-theme-alpine {
        font-family: 'Jost', sans-serif !important;
      }
      .ag-theme-alpine .ag-header {
        background: #F2F2F7 !important;
        font-size: 13px !important;
        // border-bottom: 0px !important;
        font-family: 'Jost', sans-serif !important;
         border: 1px solid #E5E5EA !important;
      }
      .ag-theme-alpine .ag-floating-filter {
        border-top: 1px solid #E5E5EA !important;
      }
        .ag-row-odd{
          background-color: #FFFFFF !important;
        }
      .ag-theme-alpine .ag-header-cell-label {
        font-weight: 500 !important;
        font-size: 12px !important;
        line-height: 133% !important;
        letter-spacing: 0 !important;
        color: #75757A !important;
        font-family: 'Jost', sans-serif !important;
      }
      .ag-theme-alpine .ag-cell {
        display: flex;
        align-items: center;
        font-size: 13px !important;
        font-weight: 400 !important;
        font-family: 'Jost', sans-serif !important;
      }
      .ag-theme-alpine .ag-paging-panel {
        justify-content: flex-end !important;
        font-size: 14px !important;
        border-top: 0px !important;
        font-family: 'Jost', sans-serif !important;
      }
      .ag-theme-alpine .ag-paging-row-summary-panel {
        justify-content: flex-start !important;
      }
      .ag-theme-alpine .ag-paging-button {
        color: #0C6FC6 !important;
        font-weight: 500 !important;
      }
      /* Protect AG Grid icon font — never override with custom font */
      .ag-theme-alpine .ag-icon {
        font-family: 'agGridAlpine', 'agGridBalham', 'agGridMaterial' !important;
        font-size: 18px !important;
      }
      .ag-theme-alpine .ag-paging-number {
        color: #2D2D2D !important;
        font-weight: 500 !important;
        margin: 0 4px !important;
        font-family: 'Jost', sans-serif !important;
      }
      .ag-theme-alpine .ag-paging-number.ag-paging-number-active {
        background: #E9FAEF !important;
        border-radius: 4px !important;
        color: #73C899 !important;
      }
      // .ag-theme-alpine .ag-paging-panel {
      //   display: flex !important;
      //   align-items: center !important;
      // }
      .ag-theme-alpine {
        // border-radius: 6px !important;
      }
      .ag-theme-alpine .ag-header-cell, .ag-theme-alpine .ag-header-group-cell {
        z-index: 0;
      }
      .ag-theme-alpine .ag-paging-row-summary-panel-number {
        font-weight: 400 !important; /* Remove bold from "Page Size" and summary numbers */
        font-family: 'Jost', sans-serif !important;
      }
      .ag-theme-alpine .ag-paging-panel label,
      .ag-theme-alpine .ag-paging-panel select {
        font-weight: 400 !important; /* Remove bold from Page Size label and dropdown */
        font-family: 'Jost', sans-serif !important;
      }
      .ag-column-select-header .ag-input-field-input {
        padding-left: 22px !important;  /* adjust padding for text */
      }
      
      /* Common AgGrid styling for all instances */
      .ag-theme-alpine .ag-root-wrapper {
        border: none !important;
        border-radius: 0px !important;
      }
      .ag-theme-alpine .ag-row {
        border-bottom: 4px solid #f8f8f8 !important;
        border-top: 4px solid #f8f8f8 !important;
        transition: background-color 0.2s ease, box-shadow 0.2s ease, border-left 0.2s ease !important;
        cursor: pointer !important;
      }
      .ag-theme-alpine .ag-row:hover {
        background-color: #f8faff !important;
        box-shadow: 0 2px 8px rgba(12, 111, 198, 0.1) !important;
        // border-left: 4px solid #0C6FC6 !important;
      }
      .ag-theme-alpine .ag-floating-filter-button {
        display: none !important;
      }
      .ag-theme-alpine .ag-cell.ag-cell-last-left-pinned {
        border-right: 0px !important;
      }
      .ag-theme-alpine .ag-header-cell.ag-header-cell-last-left-pinned {
        border-right: 0px !important;
      }
      .ag-theme-alpine .ag-pinned-left-header {
        border-right: 0px !important;
      }
      .ag-theme-alpine .ag-header-cell .ag-header-cell-menu-button {
        opacity: 0;
        transition: opacity 0.2s ease !important;
      }
      .ag-theme-alpine .ag-header-cell:hover .ag-header-cell-menu-button {
        opacity: 1;
      }
      
      /* Hide separator icon by default, show on hover */
      .ag-theme-alpine .ag-header-cell .ag-header-cell-resize {
        opacity: 0;
        transition: opacity 0.2s ease !important;
      }
      .ag-theme-alpine .ag-header-cell:hover .ag-header-cell-resize {
        opacity: 1;
      }
      
      /* Hide horizontal scrollbar */
      .ag-theme-alpine .ag-body-horizontal-scroll {
        display: block !important;
      }
      .ag-theme-alpine .ag-horizontal-left-spacer,
      .ag-theme-alpine .ag-horizontal-right-spacer {
        display: none !important;
      }
    `;
    document.head.appendChild(styleTag);
    return () => { document.head.removeChild(styleTag); };
  }, []);

  // Set datasource for infinite row model if provided
  useEffect(() => {
    if (rowModelType === 'infinite' && datasource && gridRef.current?.api) {
      gridRef.current.api.setDatasource(datasource);
    }
  }, [rowModelType, datasource]);

  return (
    <div
      className={`ag-theme-alpine ${className}`}
      style={{ width: '100%', height: gridHeight, ...style }}
    >
      <AgGridReact
        ref={gridRef}
        rowData={rowModelType === 'infinite' ? undefined : rowData}
        columnDefs={processedColumnDefs}
        domLayout={domLayout as DomLayoutType}
        // In readOnly mode omit rowSelection prop entirely to disable it
        {...(!readOnly && { rowSelection: rest.rowSelection || 'multiple' })}
        pagination={pagination && rowModelType !== 'infinite'}
        paginationPageSize={paginationPageSize}
        paginationPageSizeSelector={paginationPageSizeOptions}
        paginationNumberFormatter={params => `${params.value}`}
        suppressPaginationPanel={suppressPaginationPanel}
        rowHeight={rowHeight}
        suppressContextMenu={suppressContextMenu}
        rowModelType={rowModelType as RowModelType}
        onGridReady={onGridReady}
        suppressRowClickSelection={readOnly || rest.suppressRowClickSelection}
        suppressCellFocus={readOnly || rest.suppressCellFocus}
        readOnlyEdit={false} /* Ensure AG Grid doesn't think it's in edit */
        headerHeight={34}
        tooltipShowDelay={500}        // Show tooltip after 500ms (default is 2000)
        tooltipMouseTrack={true}
        {...rest}
      />
    </div>
  );
};

export default AgGridTable;